import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { CalendarCheckIcon, ChevronLeftIcon, ChevronRightIcon, ListFilterIcon, MessageSquareTextIcon, PauseIcon, PlayIcon, UsersIcon } from 'lucide-react';
import { conferenceCampaigns } from '../data/landing';
const campaignStages = [{
  label: 'Attendee universe built',
  Icon: UsersIcon
}, {
  label: 'ICP accounts prioritized',
  Icon: ListFilterIcon
}, {
  label: 'Replies qualified',
  Icon: MessageSquareTextIcon
}, {
  label: 'Meetings sent to reps',
  Icon: CalendarCheckIcon
}] as const;
export function HeroCampaignCarousel() {
  const shouldReduceMotion = useReducedMotion();
  const [activeIndex, setActiveIndex] = useState(0);
  const [isManuallyPaused, setIsManuallyPaused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const isPaused = isManuallyPaused || isHovered;
  const campaign = conferenceCampaigns[activeIndex];
  useEffect(() => {
    if (shouldReduceMotion || isPaused) return;
    const interval = window.setInterval(() => {
      setActiveIndex(current => (current + 1) % conferenceCampaigns.length);
    }, 4400);
    return () => window.clearInterval(interval);
  }, [isPaused, shouldReduceMotion]);
  const move = (direction: -1 | 1) => {
    setIsManuallyPaused(true);
    setActiveIndex(current => (current + direction + conferenceCampaigns.length) % conferenceCampaigns.length);
  };
  return <figure className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl shadow-gray-900/10" onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} data-id="element-114" data-mp="3w046u0y64ypv">
      <div className="flex min-h-16 items-center justify-between gap-4 border-b border-gray-200 px-5" data-id="element-115" data-mp="3m2gbb8sqnn6n">
        <div data-id="element-116" data-mp="1fc0fs4bjfbm5">
          <p className="text-sm font-bold text-gray-950" data-id="element-117" data-mp="1fbgv4fui694f">Exhibitor campaigns</p>
          <p className="text-xs text-gray-500" data-id="element-118" data-mp="1k8z5mao64wej">Pre-show business development</p>
        </div>
        <div className="flex items-center gap-1" data-id="element-119" data-mp="1it4tyudy65mg">
          <button type="button" onClick={() => move(-1)} className="flex h-11 w-11 items-center justify-center rounded-lg text-gray-600 transition-colors duration-150 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-600" aria-label="Show previous campaign" data-id="element-120" data-mp="1iqs7q515p93l">
            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-121" data-mp="14uofgh9ufatr" />
          </button>
          <button type="button" onClick={() => setIsManuallyPaused(value => !value)} className="flex h-11 w-11 items-center justify-center rounded-lg text-gray-600 transition-colors duration-150 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-600" aria-label={isManuallyPaused ? 'Resume campaign rotation' : 'Pause campaign rotation'} aria-pressed={isManuallyPaused} data-id="element-122" data-mp="21i1h8eaxfxv6">
            {isManuallyPaused ? <PlayIcon className="h-4 w-4" aria-hidden="true" data-id="element-123" data-mp="2kbf6c2ryla2v" /> : <PauseIcon className="h-4 w-4" aria-hidden="true" data-id="element-124" data-mp="2filz9890ppnm" />}
          </button>
          <button type="button" onClick={() => move(1)} className="flex h-11 w-11 items-center justify-center rounded-lg text-gray-600 transition-colors duration-150 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-600" aria-label="Show next campaign" data-id="element-125" data-mp="20hlheuqertu">
            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-126" data-mp="b3abzfw3azim" />
          </button>
        </div>
      </div>

      <div className="relative min-h-[430px] bg-gray-50 p-6 sm:p-8" data-id="element-127" data-mp="fxwesuo3vjr3">
        <AnimatePresence mode="wait" initial={false} data-id="element-128" data-mp="kyu3lq3mfc1k">
          <motion.div key={campaign.name} initial={shouldReduceMotion ? false : {
          opacity: 0,
          x: 18
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={shouldReduceMotion ? undefined : {
          opacity: 0,
          x: -18
        }} transition={{
          duration: 0.22,
          ease: [0.23, 1, 0.32, 1]
        }} data-id="element-129" data-mp="pxwzajxpo5wv">
            <div className="flex items-center justify-between gap-5" data-id="element-130" data-mp="13r65kbqd4bb6">
              <div className={`flex h-16 w-40 items-center justify-center rounded-xl border border-gray-200 px-4 ${campaign.darkBackground ? 'bg-gray-900' : 'bg-white'}`} data-id="element-131" data-mp="18oeowfn8yle0">
                <img src={campaign.src} alt={`${campaign.name} logo`} className="max-h-9 max-w-32 object-contain" data-id="element-132" data-mp="uzuuph0wupkf" />
              </div>
              <span className="rounded-md bg-indigo-100 px-3 py-1.5 text-xs font-bold text-indigo-800" data-id="element-133" data-mp="14sab49wk7637">
                Campaign ready
              </span>
            </div>
            <p className="mt-7 text-sm font-bold text-indigo-700" data-id="element-134" data-mp="c3d7ncx0bdui">{campaign.conference}</p>
            <h3 className="mt-2 text-2xl font-bold text-gray-950" data-id="element-135" data-mp="c56bkkg8jjyt">{campaign.name}</h3>
            <p className="mt-2 leading-7 text-gray-600" data-id="element-136" data-mp="76d1l205qb3e">Target: {campaign.audience}</p>

            <div className="mt-7 overflow-hidden rounded-xl border border-gray-200 bg-white" data-id="element-137" data-mp="m2ubrpwm1jod">
              {campaignStages.map(({
              label,
              Icon
            }) => <div key={label} className="flex items-center gap-3 border-b border-gray-200 px-4 py-3.5 last:border-b-0" data-id="element-138" data-mp="1xfwqeff5fz5w">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700" data-id="element-139" data-mp="1sgtkytv9bqxe">
                    <Icon className="h-4 w-4" aria-hidden="true" data-id="element-140" data-mp="1zzt2sv32laen" />
                  </div>
                  <span className="text-sm font-semibold text-gray-700" data-id="element-141" data-mp="251s5s3uwe5b2">{label}</span>
                  <span className="ml-auto h-2 w-2 rounded-full bg-emerald-500" aria-label="Complete" data-id="element-142" data-mp="1r1r4woemuxvl" />
                </div>)}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="flex items-center gap-2 border-t border-gray-200 px-5 py-3" aria-label="Choose an exhibitor campaign" data-id="element-143" data-mp="2isdre0bqktcn">
        {conferenceCampaigns.map((item, index) => <button key={item.name} type="button" onClick={() => {
        setActiveIndex(index);
        setIsManuallyPaused(true);
      }} className={`h-2.5 rounded-full transition-[width,background-color] duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2 ${activeIndex === index ? 'w-8 bg-indigo-600' : 'w-2.5 bg-gray-300 hover:bg-gray-400'}`} aria-label={`Show ${item.name} campaign`} aria-current={activeIndex === index ? 'true' : undefined} data-id="element-144" data-mp="29yikonuyuuuw" data-mpi={index} />)}
      </div>
    </figure>;
}